/**
 * Security Utilities for Portfolio Application
 * Implements various security measures and input validation
 */

// Input sanitization function
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove potentially dangerous characters and scripts
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframe tags
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/data:/gi, '') // Remove data URLs
    .trim();
};

// Advanced email validation
export const validateEmail = (email) => {
  // RFC 5322 compliant email regex (simplified but secure)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!email || typeof email !== 'string') return false;
  if (email.length > 254) return false; // RFC limit
  if (email.split('@').length !== 2) return false;
  
  const [localPart, domain] = email.split('@');
  if (localPart.length > 64) return false; // RFC limit for local part
  if (domain.length > 253) return false; // RFC limit for domain
  
  return emailRegex.test(email);
};

// Content validation for messages
export const validateMessage = (message) => {
  if (!message || typeof message !== 'string') return false;
  
  const sanitized = sanitizeInput(message);
  
  // Check for minimum and maximum length
  if (sanitized.length < 10 || sanitized.length > 5000) return false;
  
  // Check for suspicious patterns
  const suspiciousPatterns = [
    /\b(eval|function|script|iframe|object|embed)\s*\(/gi,
    /\b(document|window|location|navigator)\s*\./gi,
    /<[^>]*>/g, // HTML tags
    /\b(http|https|ftp):\/\/[^\s]+/gi // URLs (optional - you might want to allow these)
  ];
  
  // Check for suspicious patterns (excluding URLs which are allowed)
  for (let i = 0; i < suspiciousPatterns.length - 1; i++) {
    if (suspiciousPatterns[i].test(sanitized)) {
      return false;
    }
  }
  
  return true;
};

// Name validation
export const validateName = (name) => {
  if (!name || typeof name !== 'string') return false;
  
  const sanitized = sanitizeInput(name);
  
  // Allow letters, spaces, hyphens, apostrophes, and common international characters
  const nameRegex = /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF\s'-]{1,50}$/;
  
  return nameRegex.test(sanitized) && sanitized.length >= 1 && sanitized.length <= 50;
};

// Rate limiting implementation (client-side)
class RateLimiter {
  constructor() {
    this.attempts = new Map();
    this.maxAttempts = 5;
    this.windowMs = 15 * 60 * 1000; // 15 minutes
  }

  isAllowed(identifier = 'default') {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || [];
    
    // Clean old attempts outside the window
    const validAttempts = userAttempts.filter(time => now - time < this.windowMs);
    
    if (validAttempts.length >= this.maxAttempts) {
      return false;
    }
    
    validAttempts.push(now);
    this.attempts.set(identifier, validAttempts);
    return true;
  }

  getRemainingAttempts(identifier = 'default') {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || [];
    const validAttempts = userAttempts.filter(time => now - time < this.windowMs);
    
    return Math.max(0, this.maxAttempts - validAttempts.length);
  }

  getResetTime(identifier = 'default') {
    const userAttempts = this.attempts.get(identifier) || [];
    if (userAttempts.length === 0) return null;
    
    const oldestAttempt = Math.min(...userAttempts);
    return new Date(oldestAttempt + this.windowMs);
  }
}

// Create a singleton rate limiter instance
export const rateLimiter = new RateLimiter();

// Content Security Policy helper
export const getCSPDirectives = () => ({
  'default-src': "'self'",
  'script-src': "'self' 'unsafe-inline' https://api.emailjs.com",
  'style-src': "'self' 'unsafe-inline' https://fonts.googleapis.com",
  'font-src': "'self' https://fonts.gstatic.com",
  'img-src': "'self' data: https:",
  'connect-src': "'self' https://api.emailjs.com",
  'frame-src': "'none'",
  'object-src': "'none'",
  'base-uri': "'self'",
  'form-action': "'self'",
  'upgrade-insecure-requests': '',
});

// Security headers for S3 deployment
export const getSecurityHeaders = () => ({
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
});

// Environment variable validation
export const validateEnvironmentVariables = () => {
  const requiredVars = [
    'REACT_APP_EMAILJS_SERVICE_ID',
    'REACT_APP_EMAILJS_TEMPLATE_ID',
    'REACT_APP_EMAILJS_PUBLIC_KEY'
  ];

  const missing = requiredVars.filter(varName => !process.env[varName]);
  
  if (missing.length > 0) {
    console.warn('Missing environment variables:', missing);
    return false;
  }

  // Validate format of EmailJS keys
  const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  if (serviceId && !serviceId.startsWith('service_')) {
    console.warn('Invalid EmailJS Service ID format');
    return false;
  }

  if (templateId && !templateId.startsWith('template_')) {
    console.warn('Invalid EmailJS Template ID format');
    return false;
  }

  if (publicKey && publicKey.length < 10) {
    console.warn('Invalid EmailJS Public Key format');
    return false;
  }

  return true;
};

// Secure logging function (removes sensitive data)
export const secureLog = (message, data = {}) => {
  const sanitizedData = { ...data };
  
  // Remove sensitive fields
  const sensitiveFields = ['email', 'phone', 'password', 'token', 'key', 'secret'];
  
  Object.keys(sanitizedData).forEach(key => {
    if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
      sanitizedData[key] = '[REDACTED]';
    }
  });

  console.log(message, sanitizedData);
};

// Error boundary helper
export const createSecureError = (error, userMessage = 'Something went wrong. Please try again.') => {
  // Log full error for debugging (in development)
  if (process.env.NODE_ENV === 'development') {
    console.error('Full error details:', error);
  } else {
    // In production, log minimal info
    secureLog('Application error occurred', { 
      message: error.message,
      timestamp: new Date().toISOString()
    });
  }

  // Return user-friendly error
  return new Error(userMessage);
};
